import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { TerminalShell } from "@/components/TerminalShell";
import { tradingService } from "@/services/marketService";
import { formatCurrency, formatPercent } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";

const card = "rounded-md border border-[#141A26] bg-[#0A0E16] p-4";

export default function AccountPage() {
  const { t } = useLanguage();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const load = () => tradingService.summary().then(setSummary).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const rows = [
    { id: "balance", label: t("metrics.balance"), value: formatCurrency(summary?.balance) },
    { id: "equity", label: t("metrics.equity"), value: formatCurrency(summary?.equity) },
    { id: "used-margin", label: t("metrics.usedMargin"), value: formatCurrency(summary?.used_margin) },
    { id: "free-margin", label: t("metrics.freeMargin"), value: formatCurrency(summary?.free_margin) },
    { id: "credit", label: t("metrics.credit"), value: formatCurrency(summary?.credit) },
    { id: "open-pnl", label: t("metrics.openPnl"), value: formatCurrency(summary?.open_pnl) },
    { id: "margin-level", label: t("account.marginLevel"), value: summary?.margin_level ? formatPercent(summary.margin_level) : "—" },
    { id: "leverage", label: t("metrics.leverage"), value: summary?.leverage ? `1:${summary.leverage}` : "—" },
  ];

  return (
    <TerminalShell account={summary}>
      <div data-testid="account-page" className="space-y-6 p-4 sm:p-8">
        <div>
          <h1 className="font-heading text-2xl font-light tracking-tight text-white">{t("account.title")}</h1>
          <p className="mt-1 font-mono text-xs text-[#8A97AD]">ID {summary?.account_id || "—"}</p>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {rows.map((r) => (
            <div key={r.id} data-testid={`account-metric-${r.id}`} className={card}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A97AD]">{r.label}</p>
              <p className="mt-2 font-mono text-sm text-white">{r.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Link to="/deposit" data-testid="account-deposit-link"
                className={`${card} flex items-center gap-3 text-sm text-white transition-colors hover:border-[#2563EB]/50`}>
            <ArrowDownToLine className="h-4 w-4 text-[#5B9CFF]" /> {t("account.deposits")}
          </Link>
          <Link to="/withdraw" data-testid="account-withdraw-link"
                className={`${card} flex items-center gap-3 text-sm text-white transition-colors hover:border-[#2563EB]/50`}>
            <ArrowUpFromLine className="h-4 w-4 text-[#5B9CFF]" /> {t("account.withdrawals")}
          </Link>
        </div>
      </div>
    </TerminalShell>
  );
}
